import React, { useEffect, useState } from 'react';
import { Checkbox } from '@mui/material';
import { InformationTable } from '@europrocurement/flexy-components';
import { DossierOpenSearch } from '@europrocurement/l2d-domain';
import { SxProps } from '@mui/system';
import { LegalAnnoucement } from '@europrocurement/l2d-modules/modules/Dossiers/types';
import {
    type DossierArticle,
    DossierArticleWithInvoice,
    LegalAnnouncementCaptionInfos,
    useDossierProcesses,
    useDossierProcessesActionsAndRender,
} from '@europrocurement/l2d-modules';
import DossierArticleProcessMenu, {
    DossierArticleProcessMenuItemProps,
} from '../DossierArticleProcessMenu/DossierArticleProcessMenu';
import DossierArticleProcessStatus from '../DossierArticleProcessStatus/DossierArticleProcessStatus';
import DossierArticlesProcessIcons from '../DossierArticleProcessIcons/DossierArticleProcessIcons';

export type DossierArticlesProcessesListProps = {
    articles: DossierArticleWithInvoice[];
    dossier: DossierOpenSearch;
    validatorTypeId: number;
    legalAnnoucements: Array<LegalAnnoucement>;
    getItemActions?: (article: DossierArticle) => Array<DossierArticleProcessMenuItemProps>;
    selectedIds: number[];
    setSelectedIds: React.Dispatch<React.SetStateAction<number[]>>;
};

const cellsSx: SxProps[] = [
    { width: '5%', display: 'table-cell' },
    { width: '5%' },
    { width: '40%' },
    { width: '25%' },
    { width: '15%' },
    { width: '10%' },
];

const DossierArticlesProcessesList: React.FunctionComponent<DossierArticlesProcessesListProps> =
    function ({
        articles,
        dossier,
        validatorTypeId,
        legalAnnoucements,
        getItemActions = () => [],
        selectedIds,
        setSelectedIds,
    }) {
        const [rows, setRows] = useState<(string | number | React.JSX.Element | null)[][]>([]);

        const { findProcessByArticle } = useDossierProcesses();
        const { getLegalAnnoucementByArticle } = useDossierProcessesActionsAndRender();
        const tableHeadings = ['', 'N°', 'Démarches', 'Informations', 'Statuts', ''];

        useEffect(() => {
            setRows(
                articles.map((article, index) => {
                    const process = findProcessByArticle(dossier, [validatorTypeId], article.id);
                    let updateValidatorAction: DossierArticleProcessMenuItemProps | undefined =
                        {} as DossierArticleProcessMenuItemProps;
                    if (getItemActions) {
                        const itemsActions = getItemActions(article);
                        if (itemsActions) {
                            updateValidatorAction = itemsActions.find(
                                (action) => action.label === 'Modifier le statut valideur',
                            );
                        }
                    }

                    const legalAnnouncement = getLegalAnnoucementByArticle(
                        article.id,
                        dossier,
                        validatorTypeId,
                        legalAnnoucements,
                    );

                    return [
                        <Checkbox
                            sx={{ transition: 'opacity ease-in-out 300ms', width: '100%' }}
                            checked={!!selectedIds.includes(article.id)}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    setSelectedIds((ids) => [...ids, article.id]);
                                } else {
                                    setSelectedIds((ids) => ids.filter((id) => id !== article.id));
                                }
                            }}
                        />,
                        index + 1,
                        <LegalAnnouncementCaptionInfos
                            articleLabel={article.label}
                            legalAnnouncement={legalAnnouncement}
                        />,
                        <DossierArticlesProcessIcons
                            article={article}
                            dossier={dossier}
                            validatorTypeId={validatorTypeId}
                            legalAnnoucements={legalAnnoucements}
                        />,
                        process ? (
                            <DossierArticleProcessStatus
                                statusFlag={process?.statusFlag}
                                statusLabel={process?.statusLabel}
                                onClick={() => updateValidatorAction?.onClick(article)}
                            />
                        ) : (
                            ''
                        ),
                        <DossierArticleProcessMenu
                            article={article}
                            getItemActions={getItemActions}
                        />,
                    ];
                }),
            );
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [articles, dossier, legalAnnoucements, validatorTypeId, getItemActions, selectedIds]);

        return (
            <InformationTable
                headers={tableHeadings}
                rows={rows}
                cellsSx={cellsSx}
            />
        );
    };

export default DossierArticlesProcessesList;
